import React, { useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import FixedWidthContainer from 'components/FixedWidthContainer'
import useProducts from 'hooks/useProducts'
import { fetchProducts } from 'api/products'
import { Product } from 'hooks/useProduct'
import SectionHeader from 'components/SectionHeader'
import CategoryButton from 'pages/OurProducts/CategoryButton'
import ProductCategoryGrid from 'pages/OurProducts/ProductCategoryGrid'
import { cabinetsData } from 'constants/cabinetsData'
import SuccessModal from 'pages/OurProducts/SuccessModal'
import { useTranslation } from 'react-i18next'

const OurProductsPage = () => {
  const { t } = useTranslation()
  const { hardware, richelieu } = useProducts()
  const navigate = useNavigate()
  const location = useLocation()
  const [selectedCategory, setSelectedCategory] = useState('Cabinets')
  const [successModalOpen, setSuccessModalOpen] = useState(false)

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category)
    navigate(`/our-products?category=${encodeURIComponent(category)}`)
  }

  const flattenProducts = (productsObj: any): Product[] => {
    const productList: Product[] = []

    for (const itemClass in productsObj) {
      if (Object.prototype.hasOwnProperty.call(productsObj, itemClass)) {
        const colors = productsObj[itemClass]
        for (const color in colors) {
          if (Object.prototype.hasOwnProperty.call(colors, color)) {
            const categories = colors[color]
            for (const category in categories) {
              if (Object.prototype.hasOwnProperty.call(categories, category)) {
                const subcategories = categories[category]
                for (const subcategory in subcategories) {
                  if (
                    Object.prototype.hasOwnProperty.call(
                      subcategories,
                      subcategory
                    )
                  ) {
                    const products = subcategories[subcategory]
                    productList.push(...products)
                  }
                }
              }
            }
          }
        }
      }
    }

    return productList
  }

  const handleOrderSample = async (color: string) => {
    try {
      const nestedProducts = await fetchProducts(
        'Cabinets',
        color,
        undefined,
        'Sample Door'
      )
      const flattenedProducts = flattenProducts(nestedProducts)
      navigate(`/products/${flattenedProducts[0].id}`)
    } catch (error) {
      console.error('Error ordering a sample:', error)
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const category = params.get('category')
    if (
      category === 'Cabinets' ||
      category === 'Hardware' ||
      category === 'Richelieu'
    ) {
      setSelectedCategory(category)
    }
  }, [location.search])

  useEffect(() => {
    if (location.state?.checkoutSuccess) {
      setSuccessModalOpen(true)
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  return (
    <>
      <FixedWidthContainer sx={{ my: '50px' }}>
        <SectionHeader
          title={
            <>
              {t('ourProducts.title').split(' ')[0]}
              <br />
              {t('ourProducts.title').split(' ')[1]}
            </>
          }
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '44px',
            marginBottom: '40px'
          }}
        >
          {['Cabinets', 'Hardware', 'Richelieu'].map(category => (
            <CategoryButton
              key={category}
              category={category}
              isSelected={selectedCategory === category}
              onClick={handleCategoryClick}
            />
          ))}
        </Box>

        {selectedCategory === 'Cabinets' && (
          <ProductCategoryGrid
            products={cabinetsData}
            onOrderSample={handleOrderSample}
            type='Cabinets'
          />
        )}
        {selectedCategory === 'Hardware' && (
          <ProductCategoryGrid products={hardware} type='Hardware' />
        )}
        {selectedCategory === 'Richelieu' && (
          <ProductCategoryGrid products={richelieu} type='Richelieu' />
        )}
      </FixedWidthContainer>
      <SuccessModal
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
      />
    </>
  )
}

export default OurProductsPage
